import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';
import { AuthFormLayout } from '@/components/auth/AuthFormLayout';
import { useAuthFeatureFlags } from '@/hooks/useAuthFeatureFlags';
import { useAuthForm, MIN_PASSWORD_LENGTH } from '@/hooks/useAuthForm';
import { authService } from '@/lib/services/authService';
import { analyticsService } from '@/lib/services/analyticsService';
import { logger } from '@/lib/services/logger';
import { supabase } from '@/integrations/supabase/client';
import { translateAuthError, isUserExistsError } from '@/lib/utils/authErrors';
import { useAuth } from '@/hooks/useAuth';
import { storageService } from '@/lib/services/storageService';
import { audit } from '@/lib/services/auditService';
import { subscribeToPush, isPushSupported } from '@/lib/services/webPushService';
import { getEdgeFunctionUrl } from '@/lib/config/environment';
import { getAuthCallbackUrl } from '@/lib/utils/authRedirect';
import signupTitleImg from '@/assets/signup_title.png';

const SCREEN_ID = 'AUTH-102';

const Signup = () => {
  const navigate = useSafeNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const hasEmittedView = useRef(false);
  const hasRestoredDraft = useRef(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const registrationSuccessRef = useRef(false);

  const {
    email,
    password,
    emailError,
    passwordError,
    isEmailFocused,
    isPasswordFocused,
    handleEmailChange,
    handleEmailBlur,
    handleEmailFocus,
    handlePasswordChange,
    handlePasswordBlur,
    handlePasswordFocus,
    validateEmail,
    validatePassword,
    setEmailError,
    setPasswordError,
    setEmail,
    setPassword,
  } = useAuthForm();

  const { showGoogleAuth, showAdsConsent, showVkAuth } = useAuthFeatureFlags();

  const [showPassword, setShowPassword] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [adsConsentChecked, setAdsConsentChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isVkLoading, setIsVkLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Restore draft on mount, or email from navigation state
  useEffect(() => {
    if (hasRestoredDraft.current) return;

    const draft = storageService.getSignupDraft();
    if (draft) {
      setEmail(draft.email);
      setPassword(draft.password);
      setAgreementChecked(draft.agreementChecked);
      setAdsConsentChecked(draft.adsConsentChecked);
    } else {
      // If no draft, check for email/role in navigation state
      const state = location.state as { email?: string; role?: 'adopter' | 'curator' } | null;
      const stateEmail = state?.email;
      const stateRole = state?.role;

      if (stateEmail) {
        setEmail(stateEmail);
      }
      if (stateRole === 'adopter' || stateRole === 'curator') {
        storageService.setUserRole(stateRole);
      }
    }
    // Mark as restored AFTER setting values
    hasRestoredDraft.current = true;
  }, [setEmail, setPassword, location.state]);

  // Save draft on changes (skip initial render to avoid overwriting with empty values)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    storageService.saveSignupDraft({
      email,
      password,
      agreementChecked,
      adsConsentChecked,
    });
  }, [email, password, agreementChecked, adsConsentChecked]);

  useEffect(() => {
    if (hasEmittedView.current) return;
    hasEmittedView.current = true;

    analyticsService.emit({
      event_type: 'auth_signup_view',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated && !registrationSuccessRef.current) {
      navigate('/catalog', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (registrationSuccessRef.current && isAuthenticated) {
      setIsLoading(false);
      navigate('/catalog', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const isButtonEnabled =
    email.length > 0 &&
    validatePassword(password) &&
    agreementChecked &&
    !isLoading &&
    !isGoogleLoading &&
    !emailError &&
    !passwordError;

  const handleSubmit = useCallback(async () => {
    if (!isButtonEnabled) return;

    if (!validateEmail(email)) {
      setEmailError('Проверьте, правильно ли указан email.');
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError('Минимум 8 символов, буквы и цифры.');
      return;
    }

    if (!agreementChecked) {
      return;
    }

    setIsLoading(true);
    setGlobalError(null);

    analyticsService.emit({
      event_type: 'auth_signup_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'password' },
    });

    try {
      // Pre-check if email is already registered (Supabase doesn't return error for existing emails)
      const { inUse, error: checkError } = await authService.isEmailInUse(email);
      
      if (checkError) {
        // Log warning but don't block registration if check fails
        logger.warn('Email check failed, proceeding with signup', {
          context: { feature: 'auth', screen_id: SCREEN_ID },
        });
      } else if (inUse) {
        // Email is taken — show inline error
        setEmailError('Этот email уже зарегистрирован. Попробуйте войти или используйте другой.');
        
        analyticsService.emit({
          event_type: 'auth_signup_error',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { method: 'password', error_code: 'email_already_in_use' },
        });
        
        setIsLoading(false);
        return;
      }

      const selectedRole = storageService.getUserRole() ?? undefined;
      const { user, error, session } = await authService.signUp(email, password, { role: selectedRole });

      if (error) {
        if (isUserExistsError(error)) {
          setEmailError('Этот email уже зарегистрирован. Попробуйте войти или используйте другой.');
        } else {
          setGlobalError(translateAuthError(error));
        }

        analyticsService.emit({
          event_type: 'auth_signup_error',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { method: 'password', error_code: 'signup_failed' },
        });

        setIsLoading(false);
        return;
      }

      if (user) {
        // Use session from signUp response directly (avoids race condition with getSession())
        // session is null when email confirmation is required
        const hasSession = !!session;
        // If user selected curator role during onboarding, upgrade role via authenticated RPC.
        // Only possible if session exists (email confirmation disabled).
        const curatorRole = storageService.getUserRole();
        if (curatorRole === 'curator' && hasSession) {
          try {
            const { error: roleError } = await supabase.rpc('set_initial_user_role', {
              _role: 'curator',
            });
            if (roleError) {
              logger.warn('Failed to update user role', {
                error: roleError,
                context: { feature: 'auth', screen_id: SCREEN_ID },
              });
            }
          } catch (e) {
            logger.warn('Failed to update user role', {
              error: e,
              context: { feature: 'auth', screen_id: SCREEN_ID },
            });
          }
        }

        // Save ads consent if checked (only if session exists)
        if (adsConsentChecked && hasSession) {
          try {
            await supabase
              .from('users')
              .update({
                ads_consent: true,
                ads_consent_at: new Date().toISOString(),
              })
              .eq('id', user.id);

            await supabase
              .from('activity_events')
              .insert({
                actor_user_id: user.id,
                event_type: 'ads_consent_granted',
                event_type_codifier_key: 'activity_event_type',
                entity_type: 'user',
                entity_id: user.id,
                payload_json: { source: 'signup', timestamp: new Date().toISOString() },
              });
          } catch (e) {
            logger.warn('Failed to log ads consent', { error: e, context: { feature: 'auth', screen_id: SCREEN_ID } });
          }
        }

        analyticsService.emit({
          event_type: 'auth_signup_success',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { method: 'password', ads_consent: adsConsentChecked },
        });

        // Audit: log successful registration
        audit.userRegistered(user.id, 'password');

        // Clear signup draft after successful registration
        storageService.clearSignupDraft();

        // If no session (email confirmation required), redirect to confirmation screen
        if (!hasSession) {
          setIsLoading(false);
          navigate('/auth/email-confirmation', { 
            state: { email, password }, 
            replace: true 
          });
          return;
        }

        // Session exists — user is logged in
        // Request push notifications permission (silently, don't block on failure)
        if (isPushSupported()) {
          subscribeToPush().catch(() => {
            // no-op: don't log details here to avoid leaking sensitive info
          });
        }

        // Redirect to catalog
        registrationSuccessRef.current = true;
        return;
      }

      analyticsService.emit({
        event_type: 'auth_signup_success',
        screen_id: SCREEN_ID,
        timestamp: analyticsService.timestamp(),
        payload: { method: 'password', ads_consent: adsConsentChecked },
      });

      storageService.clearSignupDraft();
      registrationSuccessRef.current = true;
    } catch (err) {
      setGlobalError(translateAuthError(err instanceof Error ? err : null));
      setIsLoading(false);
    }
  }, [
    isButtonEnabled,
    email,
    password,
    agreementChecked,
    adsConsentChecked,
    validateEmail,
    validatePassword,
    setEmailError,
    setPasswordError,
  ]);

  const handleGoogleSignup = useCallback(async () => {
    if (isLoading || isGoogleLoading || isVkLoading || !agreementChecked) return;

    setIsGoogleLoading(true);
    setGlobalError(null);

    analyticsService.emit({
      event_type: 'auth_signup_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'google' },
    });

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthCallbackUrl(),
        },
      });

      if (error) {
        setGlobalError(translateAuthError(error));
        analyticsService.emit({
          event_type: 'auth_signup_error',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { method: 'google', error_code: error.message },
        });
        setIsGoogleLoading(false);
      }
    } catch (err) {
      setGlobalError(translateAuthError(err instanceof Error ? err : null));
      setIsGoogleLoading(false);
    }
  }, [isLoading, isGoogleLoading, isVkLoading, agreementChecked]);

  const handleVkSignup = useCallback(async () => {
    if (isLoading || isGoogleLoading || isVkLoading || !agreementChecked) return;

    setIsVkLoading(true);
    setGlobalError(null);

    analyticsService.emit({
      event_type: 'auth_signup_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'vk' },
    });

    try {
      // Use custom VK OAuth edge function
      const redirectUri = `${window.location.origin}/auth/callback?provider=vk`;
      const vkAuthUrl = `${getEdgeFunctionUrl('auth-vk')}?action=init&redirect_uri=${encodeURIComponent(redirectUri)}`;
      
      // Redirect to VK OAuth
      window.location.href = vkAuthUrl;
    } catch (err) {
      setGlobalError(translateAuthError(err instanceof Error ? err : null));
      setIsVkLoading(false);
    }
  }, [isLoading, isGoogleLoading, isVkLoading, agreementChecked]);

  const handleBack = useCallback(() => {
    navigate('/onboarding/role');
  }, [navigate]);

  const handleLogin = useCallback(() => {
    navigate('/auth/login', { state: { email } });
  }, [navigate, email]);

  const handleForgotPassword = useCallback(() => {
    navigate('/auth/forgot-password', { state: { email } });
  }, [navigate, email]);

  const openPrivacyPolicy = useCallback(() => {
    window.open(
      'https://docs.google.com/document/d/1ifWfzjcp7jCxtgI_jmSiabU-WKPpUuMp0Y3HraDUhc4/edit?tab=t.0',
      '_blank'
    );
  }, []);

  const openTermsOfService = useCallback(() => {
    window.open(
      'https://docs.google.com/document/d/1VdKfVAOQxVxNnHzEV9uQ5ZVnQ8qHqR7P4yX8LxXjU3c/edit',
      '_blank'
    );
  }, []);

  const headerContent = (
    <div className="flex justify-center">
      <img
        src={signupTitleImg}
        alt=""
        className="w-full max-w-[280px] h-auto object-contain"
      />
    </div>
  );

  const formFields = (
    <>
      <div className="space-y-2">
        <label 
          htmlFor="email"
          className="block text-text-primary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
          }}
        >
          Ваш email
        </label>
        <div className="relative">
          <input
            ref={emailInputRef}
            data-testid="auth-102-input-email"
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={handleEmailChange}
            onFocus={handleEmailFocus}
            onBlur={handleEmailBlur}
            disabled={isLoading || isGoogleLoading}
            placeholder="Email"
            className={`
              w-full bg-bg-secondary text-text-primary placeholder:text-text-tertiary
              outline-none transition-all
              ${emailError && !isEmailFocused ? 'ring-2 ring-accent-pink' : ''}
            `}
            style={{
              height: '52px',
              borderRadius: '16px',
              padding: '0 16px',
              fontFamily: 'CoFo Gothic, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
            }}
          />
        </div>
        {emailError && !isEmailFocused && (
          <p 
            data-testid="auth-102-email-error"
            className="text-text-accent-pink"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {emailError}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label 
          htmlFor="password"
          className="block text-text-primary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
          }}
        >
          Ваш пароль
        </label>
        <div className="relative">
          <input
            ref={passwordInputRef}
            data-testid="auth-102-input-password"
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={handlePasswordChange}
            onFocus={handlePasswordFocus}
            onBlur={handlePasswordBlur}
            disabled={isLoading || isGoogleLoading}
            placeholder="Пароль"
            className={`
              w-full bg-bg-secondary text-text-primary placeholder:text-text-tertiary outline-none
              ${passwordError && !isPasswordFocused ? 'ring-2 ring-accent-pink' : ''}
            `}
            style={{
              height: '52px',
              borderRadius: '16px',
              padding: '0 48px 0 16px',
              fontFamily: 'CoFo Gothic, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
            }}
          />
          <button
            data-testid="auth-102-toggle-password"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary"
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {passwordError && !isPasswordFocused && (
          <p 
            data-testid="auth-102-password-error"
            className="text-text-accent-pink"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {passwordError}
          </p>
        )}
      </div>

      {/* Privacy policy checkbox */}
      <div className="flex items-start gap-3">
        <button 
          data-testid="auth-102-agreement-checkbox"
          type="button"
          onClick={() => setAgreementChecked(!agreementChecked)}
          className={`w-5 h-5 min-w-[20px] min-h-[20px] rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
            agreementChecked 
              ? 'bg-text-accent-pink border-text-accent-pink' 
              : 'border-text-tertiary bg-transparent'
          }`}
          aria-label="Принять политику конфиденциальности"
        >
          <Check className={`w-3 h-3 text-white ${agreementChecked ? 'opacity-100' : 'opacity-0'}`} />
        </button>
        <p 
          className="text-text-primary leading-tight"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '14px',
          }}
        >
          Принять{' '}
          <button 
            type="button"
            onClick={openPrivacyPolicy}
            className="text-text-accent-pink underline"
          >
            Политику конфиденциальности
          </button>
          {' '}и{' '}
          <button 
            type="button"
            onClick={openTermsOfService}
            className="text-text-accent-pink underline"
          >
            Пользовательское соглашение
          </button>
        </p>
      </div>

      {/* Ads consent checkbox - shown based on feature flag */}
      {showAdsConsent && (
        <div className="flex items-start gap-3">
          <button 
            type="button"
            onClick={() => setAdsConsentChecked(!adsConsentChecked)}
            className={`w-5 h-5 min-w-[20px] min-h-[20px] rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
              adsConsentChecked 
                ? 'bg-text-accent-pink border-text-accent-pink' 
                : 'border-text-tertiary bg-transparent'
            }`}
            aria-label="Согласие на рекламу"
          >
            <Check className={`w-3 h-3 text-white ${adsConsentChecked ? 'opacity-100' : 'opacity-0'}`} />
          </button>
          <p 
            className="text-text-primary leading-tight"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '14px',
            }}
          >
            Согласен на получение{' '}
            <button 
              type="button"
              onClick={() => window.open('https://docs.google.com/document/d/1ifWfzjcp7jCxtgI_jmSiabU-WKPpUuMp0Y3HraDUhc4/edit?tab=t.0', '_blank')}
              className="text-text-accent-pink underline"
            >
              рекламных и информационных рассылок
            </button>
          </p>
        </div>
      )}

      {!agreementChecked &&
        email.length > 0 &&
        password.length >= MIN_PASSWORD_LENGTH && (
          <p 
            className="text-text-tertiary"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '14px',
            }}
          >
            Чтобы продолжить, нужно принять условия сервиса.
          </p>
        )}
    </>
  );

  return (
    <AuthFormLayout
      testId="auth-102-screen"
      screenId={SCREEN_ID}
      onBack={handleBack}
      backTestId="auth-102-back"
      rightAction={{
        label: 'Забыли пароль?',
        onClick: handleForgotPassword,
        testId: 'auth-102-link-forgot',
      }}
      headerContent={headerContent}
      error={globalError || undefined}
      errorTestId="auth-102-global-error"
      primaryAction={{
        label: 'Продолжить',
        onClick: handleSubmit,
        disabled: !isButtonEnabled,
        loading: isLoading,
        testId: 'auth-102-cta-primary',
      }}
      afterAction={
        (showGoogleAuth || showVkAuth) ? (
          <div className="space-y-3">
            {showGoogleAuth && (
              <button
                data-testid="auth-102-cta-google"
                type="button"
                onClick={handleGoogleSignup}
                disabled={isLoading || isGoogleLoading || isVkLoading || !agreementChecked}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  height: '52px',
                  borderRadius: '24px',
                  fontFamily: 'CoFo Gothic, sans-serif',
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Продолжить с Google
                  </>
                )}
              </button>
            )}
            {showVkAuth && (
              <button
                data-testid="auth-102-cta-vk"
                type="button"
                onClick={handleVkSignup}
                disabled={isLoading || isGoogleLoading || isVkLoading || !agreementChecked}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  height: '52px',
                  borderRadius: '24px',
                  fontFamily: 'CoFo Gothic, sans-serif',
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                {isVkLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4C75A3"
                        d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.576-1.496c.588-.19 1.341 1.26 2.14 1.818.605.422 1.064.33 1.064.33l2.137-.03s1.117-.071.587-.964c-.043-.073-.308-.661-1.588-1.87-1.34-1.264-1.16-1.059.453-3.246.983-1.332 1.376-2.145 1.253-2.493-.117-.332-.84-.244-.84-.244l-2.406.015s-.178-.025-.31.056c-.13.079-.212.263-.212.263s-.382 1.03-.89 1.906c-1.07 1.843-1.5 1.94-1.675 1.826-.407-.267-.305-1.07-.305-1.642 0-1.784.267-2.528-.52-2.72-.26-.063-.452-.105-1.12-.112-.857-.009-1.582.003-1.992.207-.273.136-.484.44-.356.457.158.022.516.098.706.36.245.34.237 1.1.237 1.1s.14 2.098-.328 2.358c-.321.178-.762-.185-1.71-1.85-.486-.852-.853-1.794-.853-1.794s-.07-.178-.197-.273c-.153-.115-.368-.151-.368-.151l-2.286.015s-.343.01-.47.161c-.112.135-.009.413-.009.413s1.79 4.258 3.817 6.403c1.858 1.967 3.968 1.838 3.968 1.838h.956z"
                      />
                    </svg>
                    Продолжить с VK
                  </>
                )}
              </button>
            )}
          </div>
        ) : undefined
      }
      footer={
        <span
          className="text-text-primary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '14px',
          }}
        >
          Уже есть аккаунт?{' '}
          <button
            data-testid="auth-102-link-login"
            type="button"
            onClick={handleLogin}
            className="text-text-accent-pink underline font-semibold"
          >
            Войти
          </button>
        </span>
      }
    >
      {formFields}
    </AuthFormLayout>
  );
};

export default Signup;
